// Satır içi hedef düzenleyici (TODO-KPI-01): tür / kapsam / baz / hedef / sahip / durum / not.
// Taslak ↔ DTO dönüşümü ve doğrulama `lib/kpi-targets-ui` (`draftToUpsert`); kaydet → üst bileşen.
import { useState } from "react";
import type { ClientEntity } from "@mahir/shared";
import {
  draftToUpsert,
  KPI_TARGETS_COPY,
  TARGET_STATUS_OPTIONS,
  TARGET_TYPE_OPTIONS,
  type TargetDraft,
} from "../../../lib/kpi-targets-ui";
import { btnGhost, btnPrimary, inputStyle, labelStyle } from "../kpi-form-styles";
import type { KpiTargetUpsert } from "@mahir/shared";

const C = KPI_TARGETS_COPY.targets;

export function TargetEditor(props: {
  initial: TargetDraft;
  entities: ClientEntity[];
  unit: string | null;
  busy: boolean;
  error: string | null;
  onSave: (body: KpiTargetUpsert) => void;
  onCancel: () => void;
}): JSX.Element {
  const { initial, entities, unit, busy, error, onSave, onCancel } = props;
  const [d, setD] = useState<TargetDraft>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof TargetDraft>(k: K, v: TargetDraft[K]): void =>
    setD((p) => ({ ...p, [k]: v }));
  const field = (k: keyof TargetDraft, label: string, el: JSX.Element): JSX.Element => (
    <label style={{ display: "grid", gap: 4 }}>
      <span style={labelStyle}>{label}</span>
      {el}
      {errors[k] ? (
        <span className="text-xs" style={{ color: "var(--color-error)" }}>
          {errors[k]}
        </span>
      ) : null}
    </label>
  );
  const isRel = d.targetType === "relative";
  const submit = (): void => {
    const r = draftToUpsert(d);
    if (r.errors) {
      setErrors(r.errors);
      return;
    }
    setErrors({});
    onSave(r.body);
  };
  return (
    <form
      aria-label="Hedef düzenleyici"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="rounded-xl p-3"
      style={{ background: "var(--color-bg)", border: "1px solid var(--color-border)" }}
    >
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}
      >
        {field(
          "targetType",
          C.fields.type,
          <select
            style={inputStyle}
            value={d.targetType}
            onChange={(e) => set("targetType", e.target.value as TargetDraft["targetType"])}
          >
            {TARGET_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>,
        )}
        {field(
          "entityId",
          C.fields.entity,
          <select
            style={inputStyle}
            value={d.entityId}
            onChange={(e) => set("entityId", e.target.value)}
          >
            <option value="">{C.consolidated}</option>
            {entities
              .filter((e) => e.isActive)
              .map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
          </select>,
        )}
        {field(
          "baselineYear",
          C.fields.baselineYear,
          <input
            style={inputStyle}
            inputMode="numeric"
            placeholder="2023"
            value={d.baselineYear}
            onChange={(e) => set("baselineYear", e.target.value)}
          />,
        )}
        {field(
          "baselineValue",
          C.fields.baselineValue,
          <input
            style={inputStyle}
            inputMode="decimal"
            placeholder={unit ?? ""}
            value={d.baselineValue}
            onChange={(e) => set("baselineValue", e.target.value)}
          />,
        )}
        {field(
          "targetYear",
          C.fields.targetYear,
          <input
            style={inputStyle}
            inputMode="numeric"
            value={d.targetYear}
            onChange={(e) => set("targetYear", e.target.value)}
          />,
        )}
        {isRel
          ? field(
              "targetChangePct",
              C.fields.targetChangePct,
              <input
                style={inputStyle}
                inputMode="decimal"
                placeholder="-30"
                value={d.targetChangePct}
                onChange={(e) => set("targetChangePct", e.target.value)}
              />,
            )
          : field(
              "targetValue",
              C.fields.targetValue,
              <input
                style={inputStyle}
                inputMode="decimal"
                placeholder={unit ?? ""}
                value={d.targetValue}
                onChange={(e) => set("targetValue", e.target.value)}
              />,
            )}
        {field(
          "owner",
          C.fields.owner,
          <input
            style={inputStyle}
            value={d.owner}
            onChange={(e) => set("owner", e.target.value)}
            maxLength={200}
          />,
        )}
        {field(
          "status",
          C.fields.status,
          <select
            style={inputStyle}
            value={d.status}
            onChange={(e) => set("status", e.target.value as TargetDraft["status"])}
          >
            {TARGET_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>,
        )}
        {field(
          "scope",
          C.fields.scope,
          <input
            style={inputStyle}
            value={d.scope}
            onChange={(e) => set("scope", e.target.value)}
            maxLength={32}
          />,
        )}
      </div>
      <label className="block mt-3" style={{ display: "grid", gap: 4 }}>
        <span style={labelStyle}>{C.fields.note}</span>
        <textarea
          style={{ ...inputStyle, minHeight: 56 }}
          value={d.note}
          onChange={(e) => set("note", e.target.value)}
          maxLength={2000}
        />
      </label>
      {error ? (
        <p role="alert" className="text-sm mt-2" style={{ color: "var(--color-error)" }}>
          {error}
        </p>
      ) : null}
      <div className="flex gap-2 mt-3 justify-end">
        <button type="button" style={btnGhost} onClick={onCancel} disabled={busy}>
          {C.cancel}
        </button>
        <button type="submit" style={btnPrimary} disabled={busy}>
          {busy ? C.saving : C.save}
        </button>
      </div>
    </form>
  );
}
